// Libs
import { Avatar, Box, Button, Stack, Tooltip, Typography, Zoom } from '@mui/material';
import classNames from 'classnames/bind';
import moment, { unitOfTime } from 'moment';
import { ReactElement, memo, useEffect, useState } from 'react';
import { Event, EventProps, View, Views, ViewsProps } from 'react-big-calendar';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseAvatarGroup, ConfirmModal, Loading } from '~/components/common';
import BaseCalendar, { BaseCalendarProps } from '~/components/common/baseCalendar/BaseCalendar';
import BaseCalendarDay, {
  BaseCalendarDayGroup,
  BaseCalendarDayItem,
} from '~/components/common/baseCalendarDay/BaseCalendarDay';
import {
  EventContainer,
  FormScheduleModal,
  MoreEventModal,
  ScheduleDetailModal,
} from '~/components/module/schedules';
// Others
import { varColor } from '~/themes/themeVariables';
import { customerActions } from '~/thunks/customer/customerSlice';
import { estimateActions } from '~/thunks/estimate/estimateSlice';
import { jobActions } from '~/thunks/job/jobSlice';
import { scheduleActions } from '~/thunks/schedule/scheduleSlice';
import { deleteSchedule } from '~/thunks/schedule/scheduleThunk';
import { EMPTY_STRING } from '~/utils/constants/common';
import { StorageEnum } from '~/utils/enum/base';
import { BusinessTypeEnum, BusinessTypeScheduleEnum, RoleEnum } from '~/utils/enum/common';
import { getFullName, getInitials } from '~/utils/helper/common';
import { IAssignee, ISchedule, IScheduleTimeLine } from '~/utils/interface/schedule';
import { useAppDispatch } from '~/utils/redux/hooks';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { CalendarType, ScheduleMode } from '~/utils/type/common';
// Styles, images, icons
import { icons } from '~/assets';
import styles from './Calendar.module.scss';

type CalendarProps = Omit<BaseCalendarProps, 'view' | 'date' | 'onSelectEvent' | 'events'> & {
  type?: CalendarType;
  view: View;
  date: string;
  events: ISchedule[];
  timeLineEvents: IScheduleTimeLine[];
  onSelectEvent: (event: ISchedule) => void;
  scheduleMode?: ScheduleMode;
};

const cx = classNames.bind(styles);

const views: ViewsProps = {
  month: true,
  week: true,
  day: true,
};

const renderEventIcon = (businessType?: string) => {
  switch (businessType) {
    case BusinessTypeEnum.JOB:
      return icons.menuIconJobsInactive;

    case BusinessTypeEnum.ESTIMATE:
      return icons.menuIconEstimateInactive;

    case BusinessTypeEnum.EVENT:
      return icons.menuIconScheduleInactive;

    case BusinessTypeEnum.TASK:
      return icons.menuIconTasksInactive;

    default:
      return EMPTY_STRING;
  }
};

const renderAssigneesEventComponent = (assignees: IAssignee[] | null, eventColor: string) => {
  if (!assignees) return null;

  return (
    <Box component={'div'} className={cx('avatarGroup')}>
      <BaseAvatarGroup
        avatars={assignees?.map((item) => ({
          avatarImage: item.avatar,
          firstName: item.firstName,
          lastName: item.lastName,
        }))}
        spacing={6}
        size={24}
        max={5}
        borderColor={varColor.backgroundMenuIconSubmenu}
      />
    </Box>
  );
};

const SingleEventView = memo((props: { event: ISchedule }) => {
  const { event } = props;
  const eventColor = event?.displayColor;

  return (
    <Box
      className={`event-container event-container-detailed-type`}
      sx={{ borderWidth: 1, borderColor: eventColor, backgroundColor: eventColor }}
    >
      <Tooltip
        title={event?.eventName || EMPTY_STRING}
        arrow
        placement='top'
        slots={{
          transition: Zoom,
        }}
      >
        <Stack direction={'row'} alignItems={'center'} gap={'8px'} className={cx('eventGroup')}>
          <Box component={'div'} className={cx('eventIcon')}>
            {renderEventIcon(event?.businessType) && (
              <img
                src={renderEventIcon(event?.businessType)}
                alt={cx('common_alt_img', { ns: 'common/base' })}
                className={cx('eventIconImg')}
              />
            )}
          </Box>

          <Typography className={cx('eventName')}>{event?.eventName || EMPTY_STRING}</Typography>
        </Stack>
      </Tooltip>

      <Typography className={cx('eventTime')}>
        {moment(event.startTime).format('hh:mm A')} - {moment(event.endTime).format('hh:mm A')}
      </Typography>

      {event.assignees && renderAssigneesEventComponent(event.assignees, eventColor)}
    </Box>
  );
});

const MultipleEventView = memo((props: { event: ISchedule }) => {
  const { event } = props;
  const eventColor = event?.displayColor;

  return (
    <Box
      component={'div'}
      className={`event-container event-container-title-type`}
      sx={{ borderWidth: 1, borderColor: eventColor, backgroundColor: eventColor }}
    >
      <Tooltip
        title={event?.eventName || EMPTY_STRING}
        arrow
        placement='top'
        slots={{
          transition: Zoom,
        }}
      >
        <Stack direction={'row'} alignItems={'center'} gap={'8px'} className={cx('eventGroup')}>
          <Box component={'div'} className={cx('eventIcon')}>
            {renderEventIcon(event?.businessType) && (
              <img
                src={renderEventIcon(event?.businessType)}
                alt={cx('common_alt_img', { ns: 'common/base' })}
                className={cx('eventIconImg')}
              />
            )}
          </Box>

          <Typography className={cx('eventName')}>{event?.eventName || EMPTY_STRING}</Typography>
        </Stack>
      </Tooltip>
    </Box>
  );
});

const VerticalMultipleEventView = memo(({ event }: { event: ISchedule }) => {
  const eventColor: string = event?.displayColor;
  return (
    <Box
      className={`event-container event-container-title-type`}
      sx={{ borderWidth: 1, borderColor: eventColor, backgroundColor: eventColor }}
    >
      <Tooltip
        title={event?.eventName || EMPTY_STRING}
        arrow
        placement='top'
        slots={{
          transition: Zoom,
        }}
      >
        <Stack direction={'row'} alignItems={'center'} gap={'8px'} className={cx('eventGroup')}>
          <Box component={'div'} className={cx('eventIcon')}>
            {renderEventIcon(event?.businessType) && (
              <img
                src={renderEventIcon(event?.businessType)}
                alt={cx('common_alt_img', { ns: 'common/base' })}
                className={cx('eventIconImg')}
              />
            )}
          </Box>

          <Typography className={cx('eventName')}>{event?.eventName || EMPTY_STRING}</Typography>
        </Stack>
      </Tooltip>
    </Box>
  );
});

const VerticalSingleEventView = memo(({ event }: { event: ISchedule }) => {
  const eventColor: string = event?.displayColor;

  return (
    <Box
      className={cx(`event-container event-container-detailed-type`)}
      sx={{
        borderColor: eventColor,
        backgroundColor: eventColor,
      }}
    >
      <Box className='flex flex-col flex-1 w-full gap-[6px] mb-3'>
        <Tooltip
          title={event?.eventName || EMPTY_STRING}
          arrow
          placement='top'
          slots={{
            transition: Zoom,
          }}
        >
          <Stack direction={'row'} alignItems={'center'} gap={'8px'} className={cx('eventGroup')}>
            <Box component={'div'} className={cx('eventIcon')}>
              {renderEventIcon(event?.businessType) && (
                <img
                  src={renderEventIcon(event?.businessType)}
                  alt={cx('common_alt_img', { ns: 'common/base' })}
                  className={cx('eventIconImg')}
                />
              )}
            </Box>

            <Typography className={cx('eventName')}>{event?.eventName || EMPTY_STRING}</Typography>
          </Stack>
        </Tooltip>

        <Typography className={cx('eventTime')}>
          {moment(event.startTime).format('hh:mm A')} - {moment(event.endTime).format('hh:mm A')}
        </Typography>
      </Box>

      {event.assignees && renderAssigneesEventComponent(event.assignees, eventColor)}
    </Box>
  );
});

const DayColumnHeader = memo((props: any) => {
  const tl = props.resource?.resource as IScheduleTimeLine;
  if (!tl) return <Box>{props.label}</Box>;

  const name = getFullName({ firstName: tl.firstName, lastName: tl.lastName });
  const displayColor = tl.displayColor;
  const initialName = getInitials({ firstName: tl.firstName, lastName: tl.lastName });

  return (
    <Tooltip title={name} arrow placement='top' slots={{ transition: Zoom }}>
      <Box className='flex flex-col items-center gap-[6px] py-3 px-3 cursor-default'>
        {tl.avatarImage ? (
          <Avatar
            src={tl.avatarImage}
            sx={{ width: 36, height: 36, border: `1px solid ${varColor.backgroundMenuIconSubmenu}` }}
          />
        ) : (
          <Avatar
            sx={{ bgcolor: displayColor || 'gray', width: 36, height: 36, border: `1px solid ${varColor.backgroundMenuIconSubmenu}` }}
          >
            {initialName}
          </Avatar>
        )}
        <Typography component={'p'} className='truncate text-center max-w-[80px]' sx={{ fontSize: 11, lineHeight: 1.3 }}>
          {name}
        </Typography>
      </Box>
    </Tooltip>
  );
});

const DayLeftColumn = memo((props: BaseCalendarDayGroup<IScheduleTimeLine>) => {
  const name = getFullName({ ...props.resource });
  const initialName = getInitials({
    firstName: props?.resource?.firstName,
    lastName: props?.resource?.lastName,
  });

  return (
    <Box className='flex items-center gap-[10px] h-[100px] px-[10px]'>
      {props.resource?.avatarImage ? (
        <Avatar
          src={props.resource?.avatarImage}
          style={{ border: `1px solid ${varColor.backgroundMenuIconSubmenu}` }}
        />
      ) : (
        <Avatar
          sx={{ bgcolor: props.resource?.displayColor || 'gray' }}
          style={{ border: `1px solid ${varColor.backgroundMenuIconSubmenu}` }}
        >
          {initialName}
        </Avatar>
      )}

      <Tooltip
        title={name}
        arrow
        placement='top'
        slots={{
          transition: Zoom,
        }}
      >
        <Typography component={'p'} className='truncate'>
          {name}
        </Typography>
      </Tooltip>
    </Box>
  );
});

const Calendar = (props: CalendarProps): ReactElement => {
  //#region Destructuring Props
  const {
    type,
    view,
    date,
    events,
    timeLineEvents,
    ref,
    scheduleMode = 'full',
    onSelectEvent,
    ...restProps
  } = props;
  const currentHour = moment().hour();
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/schedule']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [calendarEvents, setCalendarEvents] = useState<Array<Event>>([]);
  const [eventGroups, setEventGroups] = useState<BaseCalendarDayGroup<IScheduleTimeLine>[]>([]);
  const [eventItems, setEventItems] = useState<BaseCalendarDayItem<ISchedule>[]>([]);

  const [dayResources, setDayResources] = useState<Array<{ id: string; title: string; resource: IScheduleTimeLine }>>([]);
  const [dayResourceEvents, setDayResourceEvents] = useState<Array<Event>>([]);

  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [scheduleSelected, setScheduleSelected] = useState<string>(EMPTY_STRING);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [showFormEvent, setShowFormEvent] = useState<boolean>(false);

  const [showMore, setShowMore] = useState<boolean>(false);
  const [moreEvents, setMoreEvents] = useState<ISchedule[]>([]);

  const [showPopupDetail, setShowPopupDetail] = useState<boolean>(false);
  const [eventDetails, setEventDetails] = useState<ISchedule>();
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (props.view === Views.MONTH) {
      setCalendarEvents(prepareMonthEventsData(props.events));
    } else if (props.view === Views.DAY || props.view === Views.WEEK) {
      setCalendarEvents(prepareDayEventsData(props.timeLineEvents));
    }
  }, [scheduleMode, props.events, props.view, props.timeLineEvents]);

  useEffect(() => {
    if (scheduleMode === 'full' && view !== 'month') {
      if (view === Views.WEEK) {
        prepareDayEventGroup(props.timeLineEvents);
      } else if (view === Views.DAY) {
        setDayResources(prepareDayResources(props.timeLineEvents));
        setDayResourceEvents(prepareDayResourceEventsData(props.timeLineEvents));
      }
    }

    if (props.view === Views.DAY || props.view === Views.WEEK) {
      setCalendarEvents(prepareDayEventsData(props.timeLineEvents));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleMode, props.timeLineEvents, view]);
  //#endregion Implement Hook

  //#region Handle Function
  const prepareMonthEventsData = (events: ISchedule[]): Array<Event> => {
    if (!Array.isArray(events) || events.length === 0) return [];

    const calendarEvents: Array<Event> = [];

    for (let event of events) {
      let monthEvent = calendarEvents.find((monthEvent: Event) => {
        return moment(monthEvent.start).isSame(moment(event.startDate), 'date');
      });

      if (!monthEvent) {
        monthEvent = {
          start: moment(event.startDate).toDate(),
          end: moment(event.startDate).endOf('date').toDate(),
          resource: [],
        };
        calendarEvents.push(monthEvent);
      }

      monthEvent.resource.push(event);
    }
    return calendarEvents;
  };

  const prepareDayEventsData = (events: IScheduleTimeLine[]): Array<Event> => {
    const result: Array<Event> = [];
    const uniqueEventIds = new Set();

    events.forEach((eventTimeLine) => {
      if (!eventTimeLine.events || eventTimeLine.events.length === 0) return;
      eventTimeLine.events.forEach((event: ISchedule) => {
        if (uniqueEventIds.has(event.id)) return;
        uniqueEventIds.add(event.id);

        const startTime = moment(event.startTime).format('HH:mm:ss');
        const endTime = moment(event.endTime).format('HH:mm:ss');
        const [hoursStart, minutesStart] = startTime.split(':').map(Number);
        const [hoursEnd, minutesEnd] = endTime.split(':').map(Number);

        const startAt = moment(event.startDate).hour(hoursStart).minute(minutesStart);

        let endAt;
        if (moment(event.startDate).isSame(moment(event.endDate), 'date')) {
          endAt = moment(event.startDate).hour(hoursEnd).minute(minutesEnd);
        } else {
          endAt = moment(event.startDate).hour(23).minute(59);
        }

        result.push({
          start: startAt.toDate(),
          end: endAt.toDate(),
          resource: [event],
        });
      });
    });
    return result;
  };

  const prepareDayEventGroup = (itemEvents: IScheduleTimeLine[]) => {
    const groups: BaseCalendarDayGroup<IScheduleTimeLine>[] = [];
    let items: BaseCalendarDayItem<ISchedule>[] = [];

    itemEvents.forEach((itemEvent: IScheduleTimeLine) => {
      groups.push({
        id: itemEvent.id,
        title: '',
        resource: itemEvent,
      });

      if (itemEvent.events) {
        itemEvent.events.forEach((item: ISchedule) => {
          const startTime = moment(item.startTime).format('HH:mm:ss');
          const [hours, minutes, seconds] = startTime.split(':').map(Number);

          const startAt = moment(item.startDate).hour(hours).minute(minutes).second(seconds);

          const endAt = moment(item.startDate).hour(hours).minute(minutes).second(seconds);

          let dateFormat =
            view === Views.DAY ? ('hour' as unitOfTime.StartOf) : ('day' as unitOfTime.StartOf);

          const eventObj: BaseCalendarDayItem<ISchedule> = {
            id: `${itemEvent.id}-${item.id}`,
            group: itemEvent.id,
            start_time: startAt.startOf(dateFormat).toDate(),
            end_time: endAt.endOf(dateFormat).toDate(),
            resource: [],
          };

          const index = items.findIndex(({ start_time, group }) => {
            return startAt.isSame(start_time, dateFormat) && itemEvent.id === group;
          });

          if (index > -1) {
            return items[index].resource.push(item);
          }

          eventObj.resource.push(item);
          items.push(eventObj);
        });
      }
    });

    setEventGroups(groups);
    setEventItems(items);
  };

  const prepareDayResources = (tleData: IScheduleTimeLine[]) => {
    const seen = new Set<string>();
    return tleData
      .filter((tl) => {
        if (seen.has(tl.id)) return false;
        seen.add(tl.id);
        return true;
      })
      .map((tl) => ({
        id: tl.id,
        title: getFullName({ firstName: tl.firstName, lastName: tl.lastName }),
        resource: tl,
      }));
  };

  const prepareDayResourceEventsData = (tleData: IScheduleTimeLine[]): Array<Event> => {
    const result: Array<Event> = [];

    tleData.forEach((tl) => {
      if (!tl.events) return;

      tl.events.forEach((event: ISchedule) => {
        const startTime = moment(event.startTime).format('HH:mm:ss');
        const endTime = moment(event.endTime).format('HH:mm:ss');
        const [hoursStart, minutesStart] = startTime.split(':').map(Number);
        const [hoursEnd, minutesEnd] = endTime.split(':').map(Number);

        const startAt = moment(event.startDate).hour(hoursStart).minute(minutesStart);
        let endAt;
        if (moment(event.startDate).isSame(moment(event.endDate), 'date')) {
          endAt = moment(event.startDate).hour(hoursEnd).minute(minutesEnd);
        } else {
          endAt = moment(event.startDate).hour(23).minute(59);
        }

        result.push({
          start: startAt.toDate(),
          end: endAt.toDate(),
          resourceId: tl.id,
          resource: [event],
        } as Event & { resourceId: string });
      });
    });

    return result;
  };

  const handleShowConfirmDelete = (scheduleId: string) => {
    setScheduleSelected(scheduleId);
    setShowConfirmDelete(true);

    handleClosePopupDetails();
  };

  const closeConfirmDelete = () => {
    setShowConfirmDelete(false);
  };

  const handleDeleteEvent = () => {
    if (!scheduleSelected) return;

    setIsLoading(true);

    dispatch(deleteSchedule(scheduleSelected))
      .unwrap()
      .then((res) => {
        switch (type) {
          case 'customer':
            dispatch(customerActions.setIsRefreshScheduleData(true));
            break;

          case 'job':
            dispatch(jobActions.setIsRefreshScheduleData(true));
            break;

          case 'estimate':
            dispatch(estimateActions.setIsRefreshScheduleData(true));
            break;

          default:
            dispatch(scheduleActions.changeNeedRefreshData(true));
            break;
        }
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
        closeConfirmDelete();
      });
  };

  const handleCloseForm = () => {
    setShowFormEvent(false);
    setScheduleSelected(EMPTY_STRING);
  };

  const handleDisplayEdit = (scheduleId: string) => {
    switch (eventDetails?.businessType) {
      case BusinessTypeScheduleEnum.ESTIMATE:
        if (currentRole === RoleEnum.ADMIN) {
          navigate(`${adminRouteAbsolute.updateEstimate(eventDetails?.businessId)}`);
        }

        if (currentRole === RoleEnum.STAFF) {
          navigate(`${staffRouteAbsolute.updateEstimate(eventDetails?.businessId)}`);
        }
        break;

      case BusinessTypeScheduleEnum.JOB:
        if (currentRole === RoleEnum.ADMIN) {
          navigate(`${adminRouteAbsolute.updateJob(eventDetails?.businessId)}`);
        }

        if (currentRole === RoleEnum.STAFF) {
          navigate(`${staffRouteAbsolute.updateJob(eventDetails?.businessId)}`);
        }
        break;

      default:
        setShowFormEvent(true);
        setScheduleSelected(scheduleId);

        handleClosePopupDetails();
        break;
    }
  };

  const navigateEventDetails = (scheduleId: string) => {
    if (!scheduleId) return;

    switch (eventDetails?.businessType) {
      case BusinessTypeScheduleEnum.ESTIMATE:
        if (currentRole === RoleEnum.ADMIN) {
          navigate(`${adminRouteAbsolute.estimate}/${eventDetails?.businessId}`);
        }

        if (currentRole === RoleEnum.STAFF) {
          navigate(`${staffRouteAbsolute.estimate}/${eventDetails?.businessId}`);
        }
        break;

      case BusinessTypeScheduleEnum.JOB:
        if (currentRole === RoleEnum.ADMIN) {
          navigate(`${adminRouteAbsolute.jobs}/${eventDetails?.businessId}`);
        }

        if (currentRole === RoleEnum.STAFF) {
          navigate(`${staffRouteAbsolute.jobs}/${eventDetails?.businessId}`);
        }
        break;

      default:
        handleClosePopupDetails();
        break;
    }
  };

  const handleShowMoreEvents = (remainEvents: ISchedule[]) => {
    if (!remainEvents) return;
    setShowMore(true);
    setMoreEvents(remainEvents);
  };

  const handleCloseMoreEvents = () => {
    setShowMore(false);
  };

  const handleShowPopupDetails = (event: ISchedule) => {
    if (!event) return;
    setEventDetails(event);
    setShowPopupDetail(true);
    handleCloseMoreEvents();
  };

  const handleClosePopupDetails = () => {
    setShowPopupDetail(false);
  };
  //#endregion Handle Function

  return (
    <Box className={cx('calendar-component')}>
      {(view === 'month' || scheduleMode === 'my') && (
        <Box
          className={cx(
            `hour-${currentHour + 1}`,
            // { 'h-[735px]': view === Views.MONTH },
            { 'calendar-day-view': view === Views.DAY },
            { 'calendar-week-view': view === Views.WEEK }
          )}
        >
          <BaseCalendar
            events={calendarEvents}
            selectable={true}
            toolbar={false}
            date={date}
            views={views}
            view={view}
            components={{
              event: ({ event }: EventProps) => {
                if (view === Views.MONTH) {
                  const allEvent = (event?.resource as ISchedule[]) || [];
                  const isMultiple = allEvent && allEvent?.length > 1;

                  const eventsToShow = allEvent.slice(0, 3);
                  const remainingEvents = allEvent.slice(3);

                  const isShowBtn = remainingEvents.length > 0;

                  return (
                    <>
                      <Box className={cx('eventMonthFull')}>
                        {eventsToShow.map((eventData: ISchedule, index: number) => {
                          return (
                            <EventContainer key={index} onClick={() => handleShowPopupDetails(eventData)}>
                              {isMultiple ? (
                                <MultipleEventView event={eventData} />
                              ) : (
                                <SingleEventView event={eventData} />
                              )}
                            </EventContainer>
                          );
                        })}
                      </Box>

                      {isShowBtn && (
                        <Button
                          className={cx('btnMore')}
                          onClick={() => handleShowMoreEvents(remainingEvents)}
                        >
                          {t('schedule_button_show_more', { number: remainingEvents?.length })}
                        </Button>
                      )}
                    </>
                  );
                } else if (view === Views.DAY) {
                  const eventData = event.resource[0] as ISchedule;
                  const duration = moment(event.end).diff(moment(event.start), 'minutes');
                  return (
                    <EventContainer onClick={() => handleShowPopupDetails(eventData)}>
                      {duration > 60 ? (
                        <VerticalSingleEventView event={eventData} />
                      ) : (
                        <VerticalMultipleEventView event={eventData} />
                      )}
                    </EventContainer>
                  );
                } else if (view === Views.WEEK) {
                  const eventData = event.resource[0] as ISchedule;
                  const duration = moment(event.end).diff(moment(event.start), 'minutes');
                  return (
                    <EventContainer onClick={() => handleShowPopupDetails(eventData)}>
                      {duration > 60 ? (
                        <VerticalSingleEventView event={eventData} />
                      ) : (
                        <VerticalMultipleEventView event={eventData} />
                      )}
                    </EventContainer>
                  );
                }
              },
            }}
            onSelectEvent={(event, e) => {
              e.preventDefault();
            }}
            {...restProps}
          />
        </Box>
      )}

      {scheduleMode === 'full' && view === Views.WEEK && (
        <Box>
          <BaseCalendarDay<IScheduleTimeLine, ISchedule>
            date={date}
            groups={eventGroups}
            items={eventItems}
            groupRenderer={(group) => <DayLeftColumn {...group} />}
            mode={view as unitOfTime.StartOf}
            scheduleMode={scheduleMode}
            itemRenderer={(event) => {
              const allEvent = (event?.resource as ISchedule[]) || [];
              const isMultiple = allEvent && allEvent?.length > 1;

              const eventsToShow = allEvent.slice(0, 3);
              const remainingEvents = allEvent.slice(3);

              const isShowBtn = remainingEvents.length > 0;

              return (
                <>
                  <Box className='flex flex-col gap-[4px] max-h-[88px] overflow-y-auto pr-1 calendar-week-multiple-view'>
                    {eventsToShow.map((eventData: ISchedule, index: number) => {
                      return (
                        <EventContainer key={index} onClick={() => handleShowPopupDetails(eventData)}>
                          {isMultiple ? (
                            <MultipleEventView event={eventData} />
                          ) : (
                            <SingleEventView event={eventData} />
                          )}
                        </EventContainer>
                      );
                    })}
                  </Box>

                  {isShowBtn && (
                    <Button className={cx('btnMore')} onClick={() => handleShowMoreEvents(remainingEvents)}>
                      {t('schedule_button_show_more', { number: remainingEvents?.length })}
                    </Button>
                  )}
                </>
              );
            }}
          />
        </Box>
      )}

      {scheduleMode === 'full' && view === Views.DAY && (
        <Box className={cx('dayResourceView')}>
          <BaseCalendar
            view='day'
            date={date}
            selectable={false}
            toolbar={false}
            events={dayResourceEvents}
            resources={dayResources}
            resourceIdAccessor={(resource) => (resource as { id: string }).id}
            resourceTitleAccessor={(resource) => (resource as { title: string }).title}
            components={{
              resourceHeader: DayColumnHeader,
              event: ({ event }: EventProps) => {
                const eventData = event.resource[0] as ISchedule;
                const duration = moment(event.end).diff(moment(event.start), 'minutes');
                return (
                  <EventContainer onClick={() => handleShowPopupDetails(eventData)}>
                    {duration > 60 ? (
                      <VerticalSingleEventView event={eventData} />
                    ) : (
                      <VerticalMultipleEventView event={eventData} />
                    )}
                  </EventContainer>
                );
              },
            }}
            onSelectEvent={(event, e) => {
              e.preventDefault();
            }}
          />
        </Box>
      )}

      {showConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={closeConfirmDelete}
          onConfirm={handleDeleteEvent}
        />
      )}
      {showFormEvent && <FormScheduleModal onClose={handleCloseForm} scheduleId={scheduleSelected} />}
      {showMore && (
        <MoreEventModal
          eventList={moreEvents}
          onSelectEvent={handleShowPopupDetails}
          onClose={handleCloseMoreEvents}
        />
      )}
      {showPopupDetail && (
        <ScheduleDetailModal
          data={eventDetails}
          onEditSchedule={handleDisplayEdit}
          onDeleteSchedule={handleShowConfirmDelete}
          onShowDetail={navigateEventDetails}
          onClose={handleClosePopupDetails}
        />
      )}

      {isLoading && <Loading />}
    </Box>
  );
};

export default memo(Calendar);
